/**
 * AI model prices grouped by model; history collapsed by default. TMMPDM-0035: one model's history
 * is open at a time — the page's URL `history`. TMMPDM-0098: a future row is shown with the rows
 * in force, and Withdraw… is offered on a future row that is not withdrawn. The server decides.
 * TMMPDM-0104: rendered per catalogue model (no Model column, its own name and empty line), and
 * for the prices of models outside the catalogue (with the Model column, no Change price…).
 */
import { Fragment, useRef, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import {
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ApiError, errorCopy } from '@/api/errors';
import { adminApi } from '@/api/tmm/admin';
import { useCopy } from '@/hooks/useCopy';
import { ConfirmDialog, EmptyState } from '@/components/page';
import { Amount } from '@/components/ui';
import { NOT_SENT, formatDate, formatUtcMinute as formatUtc } from '@/format';
import type { AiModelPriceRow } from '@/api/tmm/types';
import { StatusChip } from '@/status';

const NOTE_MAX = 500;

function notWithdrawn(row: AiModelPriceRow): boolean {
  return row.withdrawnAt == null || row.withdrawnAt === '';
}

/** A future row that can be withdrawn. Not an `inForce` verdict. */
function isScheduledPrice(row: AiModelPriceRow): boolean {
  return notWithdrawn(row) && Date.parse(row.effectiveFrom) > Date.now();
}

function shownByDefault(row: AiModelPriceRow): boolean {
  return row.inForce === true || isScheduledPrice(row);
}

interface ModelPricesTableProps {
  rows: AiModelPriceRow[];
  loading: boolean;
  locale: string;
  /** The model whose history is expanded, if any. */
  historyModel: string | undefined;
  onToggleHistory: (model: string) => void;
  /** Opens change mode for a row the server marked in force. Absent: no row action. */
  onChangePrice?: (row: AiModelPriceRow) => void;
  /** Re-read the price list after a withdraw, including a 409 or 404 that changes the row. */
  onReload?: () => void;
  /** The table's accessible name. */
  ariaLabel: string;
  /** False leaves out the Model column, where the table holds one model's prices. Default true. */
  showModel?: boolean;
  /** Shown when there are no rows. */
  emptyTitle: string;
}

function groupsOf(rows: AiModelPriceRow[]): { model: string; rows: AiModelPriceRow[] }[] {
  const byModel = new Map<string, AiModelPriceRow[]>();
  for (const row of rows) {
    const list = byModel.get(row.model) ?? [];
    list.push(row);
    byModel.set(row.model, list);
  }
  return [...byModel.entries()].map(([model, group]) => ({
    model,
    rows: [...group].sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom)),
  }));
}

export function ModelPricesTable({
  rows,
  loading,
  locale,
  historyModel,
  onToggleHistory,
  onChangePrice,
  onReload,
  ariaLabel,
  showModel = true,
  emptyTitle,
}: ModelPricesTableProps) {
  const { t } = useCopy();
  const [withdrawing, setWithdrawing] = useState<AiModelPriceRow | null>(null);

  const body = loading ? (
    <Typography variant="body2" color="text.secondary">
      {t('common.loading', 'Loading')}
    </Typography>
  ) : rows.length === 0 ? (
    <EmptyState surface title={emptyTitle} />
  ) : (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small" aria-label={ariaLabel}>
        <TableHead>
          <TableRow>
            {showModel ? <TableCell>{t('ai.col.model', 'Model')}</TableCell> : null}
            <TableCell>{t('ai.col.currency', 'Currency')}</TableCell>
            <TableCell>{t('ai.col.effectiveFrom', 'Effective from')}</TableCell>
            <TableCell>{t('ai.col.inForce', 'In force')}</TableCell>
            <TableCell align="right">{t('ai.col.input', 'Input / Mtok')}</TableCell>
            <TableCell align="right">{t('ai.col.output', 'Output / Mtok')}</TableCell>
            <TableCell align="right">{t('ai.col.cacheRead', 'Cache read / Mtok')}</TableCell>
            <TableCell align="right">{t('ai.col.cacheWrite', 'Cache write / Mtok')}</TableCell>
            <TableCell>{t('ai.col.added', 'Added')}</TableCell>
            <TableCell>{t('datedSettings.col.setBy', 'Set by')}</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {groupsOf(rows).map((group) => {
            const showHistory = historyModel === group.model;
            const defaultRows = group.rows.filter(shownByDefault);
            const visible = showHistory || defaultRows.length === 0 ? group.rows : defaultRows;
            const hasHistory = group.rows.some((row) => !shownByDefault(row));
            return visible.map((row, index) => (
              <Fragment key={row.id}>
                <TableRow hover>
                  {showModel ? (
                    <TableCell>
                      {index === 0 ? (
                        <Typography
                          variant="body2"
                          sx={(theme) => ({ fontWeight: 600, ...theme.typography.mono })}
                        >
                          {group.model}
                        </Typography>
                      ) : null}
                    </TableCell>
                  ) : null}
                  <TableCell>{row.currency}</TableCell>
                  <TableCell>{formatDate(row.effectiveFrom, locale)}</TableCell>
                  <TableCell>
                    <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
                      {row.inForce === true ? (
                        <StatusChip
                          tone="success"
                          icon={<CheckIcon />}
                          label={t('ai.inForce', 'In force')}
                        />
                      ) : null}
                      {isScheduledPrice(row) && row.inForce !== true ? (
                        <>
                          <StatusChip tone="info" label={t('ai.scheduled', 'Scheduled')} />
                          <Typography variant="caption" color="text.secondary">
                            {formatUtc(row.effectiveFrom)}
                          </Typography>
                        </>
                      ) : null}
                      {typeof row.withdrawnAt === 'string' && row.withdrawnAt !== '' ? (
                        <>
                          <StatusChip
                            tone="neutral"
                            label={t('datedSettings.withdrawn', 'Withdrawn')}
                          />
                          {typeof row.withdrawnBy === 'string' && row.withdrawnBy !== '' ? (
                            <Typography variant="caption" color="text.secondary">
                              {t('datedSettings.withdrawnBy', 'by {{who}}', {
                                who: row.withdrawnBy,
                              })}
                            </Typography>
                          ) : null}
                          {typeof row.withdrawNote === 'string' && row.withdrawNote !== '' ? (
                            <Typography variant="caption" color="text.secondary">
                              {row.withdrawNote}
                            </Typography>
                          ) : null}
                        </>
                      ) : null}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <Amount
                      micro={row.inputPerMtokMicro}
                      currency={row.currency}
                      perMtok
                      locale={locale}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Amount
                      micro={row.outputPerMtokMicro}
                      currency={row.currency}
                      perMtok
                      locale={locale}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Amount
                      micro={row.cacheReadPerMtokMicro}
                      currency={row.currency}
                      perMtok
                      locale={locale}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Amount
                      micro={row.cacheWritePerMtokMicro}
                      currency={row.currency}
                      perMtok
                      locale={locale}
                    />
                  </TableCell>
                  <TableCell>{formatDate(row.createdAt, locale)}</TableCell>
                  <TableCell>{row.createdBy !== '' ? row.createdBy : NOT_SENT}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                      {isScheduledPrice(row) ? (
                        <Button
                          size="small"
                          aria-label={t(
                            'ai.withdrawPriceFor',
                            'Withdraw the price starting {{when}}',
                            { when: formatUtc(row.effectiveFrom) },
                          )}
                          onClick={() => setWithdrawing(row)}
                        >
                          {t('datedSettings.withdraw', 'Withdraw…')}
                        </Button>
                      ) : null}
                      {row.inForce === true && onChangePrice ? (
                        <Button
                          size="small"
                          aria-label={t(
                            'ai.changePriceFor',
                            'Change price for {{name}} ({{currency}})',
                            { name: row.model, currency: row.currency },
                          )}
                          onClick={() => onChangePrice(row)}
                        >
                          {t('ai.changePrice', 'Change price…')}
                        </Button>
                      ) : null}
                      {index === 0 && hasHistory ? (
                        <Button size="small" onClick={() => onToggleHistory(group.model)}>
                          {showHistory
                            ? t('ai.hideHistory', 'Hide history')
                            : t('ai.showHistory', 'Show history')}
                        </Button>
                      ) : null}
                    </Stack>
                  </TableCell>
                </TableRow>
                {typeof row.note === 'string' && row.note !== '' ? (
                  <TableRow>
                    <TableCell colSpan={showModel ? 11 : 10}>
                      {t('datedSettings.noteLine', 'Note: {{note}}', { note: row.note })}
                    </TableCell>
                  </TableRow>
                ) : null}
              </Fragment>
            ));
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <>
      {body}
      <PriceWithdrawDialog
        row={withdrawing}
        onClose={() => setWithdrawing(null)}
        onDone={() => {
          setWithdrawing(null);
          onReload?.();
        }}
        onReread={() => onReload?.()}
      />
    </>
  );
}

function PriceWithdrawDialog({
  row,
  onClose,
  onDone,
  onReread,
}: {
  row: AiModelPriceRow | null;
  onClose: () => void;
  onDone: () => void;
  onReread: () => void;
}) {
  const { t } = useCopy();
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const [openedFor, setOpenedFor] = useState<number | null>(null);
  if (!row && openedFor !== null) setOpenedFor(null);
  if (row && row.id !== openedFor) {
    setOpenedFor(row.id);
    setNote('');
    setError(null);
  }

  const confirm = async () => {
    if (!row || inFlight.current) return;
    if (note.trim().length > NOTE_MAX) {
      setError(
        t('datedSettings.form.tooLong', 'Use at most {{max, number}} characters.', {
          max: NOTE_MAX,
        }),
      );
      return;
    }
    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      const trimmed = note.trim();
      await adminApi.withdrawAiModelPrice(row.id, trimmed === '' ? undefined : trimmed);
      onDone();
    } catch (err) {
      const code = err instanceof ApiError ? String(err.code) : '';
      if (code === 'PRICE_IN_USE') {
        setError(
          t(
            'ai.priceInUse',
            'A model assignment depends on this price. Change or withdraw that assignment first.',
          ),
        );
      } else if (code === 'SETTING_IN_EFFECT') {
        setError(
          t(
            'datedSettings.errors.inEffect',
            "This change is already in effect, so it can't be withdrawn. The list has been reloaded.",
          ),
        );
        onReread();
      } else if (code === 'NOT_FOUND' || (err instanceof ApiError && err.status === 404)) {
        setError(
          errorCopy(err, t, t('datedSettings.errors.notFound', "That setting couldn't be found.")),
        );
        onReread();
      } else {
        setError(
          errorCopy(err, t, t('datedSettings.errors.withdraw', "Couldn't withdraw this change.")),
        );
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const when = row ? formatUtc(row.effectiveFrom) : '';
  return (
    <ConfirmDialog
      open={row !== null}
      title={t('ai.withdrawPriceTitle', 'Withdraw a scheduled price')}
      consequence={
        <Stack spacing={2}>
          <span>
            {t('ai.withdrawPriceQuestion', 'Withdraw the price starting {{when}}?', { when })}
          </span>
          <TextField
            label={t('datedSettings.form.note', 'Note (optional)')}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            multiline
            minRows={2}
            slotProps={{ htmlInput: { maxLength: NOTE_MAX } }}
          />
        </Stack>
      }
      confirmLabel={t('datedSettings.withdrawConfirm', 'Withdraw')}
      destructive
      busy={busy}
      error={error}
      onConfirm={() => void confirm()}
      onCancel={onClose}
    />
  );
}
